import { z } from 'zod';
import {
  addressSchema,
  baseRow,
  cents,
  customFieldValues,
  dateOnly,
  id,
  isoDate,
  listInput,
  nonEmpty,
  nonNegativeCents,
  expectedVersion,
  optionalText,
} from './common';
import { businessType, memberRole, permissionSet } from './workspace';
import { priority } from './crm';

// ---------------------------------------------------------------------------
// Update inputs
// ---------------------------------------------------------------------------

type NoDefault<S> = S extends z.ZodDefault<infer I> ? I : S;
type PatchShape<T extends z.ZodRawShape> = { [K in keyof T]: z.ZodOptional<NoDefault<T[K]>> };

/**
 * `{ id, expectedVersion?, ...partial(create) }` where omitted fields stay `undefined`.
 * (zod 4's `.partial()` keeps inner `.default()`s, so `updateOf` would silently reset
 * defaulted columns such as `status` or `custom` on every partial update.)
 */
export function patchOf<T extends z.ZodRawShape>(create: z.ZodObject<T>) {
  const shape: Record<string, z.ZodType> = {};
  for (const [key, field] of Object.entries(create.shape)) {
    const inner = field instanceof z.ZodDefault ? (field.unwrap() as z.ZodType) : (field as unknown as z.ZodType);
    shape[key] = inner.optional();
  }
  return z.object(shape as unknown as PatchShape<T>).extend({ id, expectedVersion });
}

// ---------------------------------------------------------------------------
// Enumerations
// ---------------------------------------------------------------------------

export const PROJECT_STATUSES = ['planning', 'preconstruction', 'active', 'on_hold', 'punch', 'completed', 'closed', 'cancelled'] as const;
export const projectStatus = z.enum(PROJECT_STATUSES);
export type ProjectStatus = z.infer<typeof projectStatus>;

export const PROJECT_TYPES = ['new_home', 'remodel', 'addition', 'commercial_new', 'tenant_improvement', 'service', 'trade_package', 'other'] as const;
export const projectType = z.enum(PROJECT_TYPES);
export type ProjectType = z.infer<typeof projectType>;

export const SCHEDULE_TASK_STATUSES = ['not_started', 'in_progress', 'blocked', 'done'] as const;
export const scheduleTaskStatus = z.enum(SCHEDULE_TASK_STATUSES);

export const DEPENDENCY_TYPES = ['FS', 'SS', 'FF', 'SF'] as const;
export const dependencyType = z.enum(DEPENDENCY_TYPES);

export const DOCUMENT_FOLDERS = ['contracts', 'plans', 'permits', 'specs', 'photos', 'selections', 'warranties', 'closeout', 'insurance', 'general'] as const;
export const documentFolder = z.enum(DOCUMENT_FOLDERS);

export const DRAWING_DISCIPLINES = ['architectural', 'structural', 'civil', 'mechanical', 'electrical', 'plumbing', 'fire_protection', 'landscape', 'interior', 'other'] as const;
export const drawingDiscipline = z.enum(DRAWING_DISCIPLINES);

export const MARKUP_KINDS = ['pen', 'line', 'arrow', 'rect', 'ellipse', 'cloud', 'text', 'measure', 'pin'] as const;
export const markupKind = z.enum(MARKUP_KINDS);

export const RFI_STATUSES = ['draft', 'open', 'answered', 'closed', 'void'] as const;
export const rfiStatus = z.enum(RFI_STATUSES);
export type RfiStatus = z.infer<typeof rfiStatus>;

export const SUBMITTAL_STATUSES = ['draft', 'submitted', 'in_review', 'approved', 'approved_as_noted', 'revise_resubmit', 'rejected', 'closed'] as const;
export const submittalStatus = z.enum(SUBMITTAL_STATUSES);
export type SubmittalStatus = z.infer<typeof submittalStatus>;

export const PUNCH_STATUSES = ['open', 'in_progress', 'ready_for_review', 'closed', 'void'] as const;
export const punchStatus = z.enum(PUNCH_STATUSES);
export type PunchStatus = z.infer<typeof punchStatus>;

export const BID_PACKAGE_STATUSES = ['draft', 'open', 'leveling', 'awarded', 'cancelled'] as const;
export const bidPackageStatus = z.enum(BID_PACKAGE_STATUSES);

export const BID_STATUSES = ['invited', 'viewed', 'declined', 'submitted', 'awarded', 'not_awarded', 'withdrawn'] as const;
export const bidStatus = z.enum(BID_STATUSES);

/** Time clock: open while clocked in; submitted by the worker; approved or rejected by a PM/admin. */
export const TIME_ENTRY_STATUSES = ['open', 'submitted', 'approved', 'rejected'] as const;
export const timeEntryStatus = z.enum(TIME_ENTRY_STATUSES);
export type TimeEntryStatus = z.infer<typeof timeEntryStatus>;

export const TIME_ENTRY_SOURCES = ['mobile', 'web', 'manual'] as const;
export const timeEntrySource = z.enum(TIME_ENTRY_SOURCES);

/** Where a clock-in/out happened (device GPS). */
export const geoPoint = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  accuracyM: z.number().nonnegative().max(100_000).optional(),
});
export type GeoPoint = z.infer<typeof geoPoint>;

/** Team chat: project channel, trade channel (one sub/supplier company on a project), general or direct. */
export const CHAT_CHANNEL_KINDS = ['project', 'trade', 'general', 'direct'] as const;
export const chatChannelKind = z.enum(CHAT_CHANNEL_KINDS);
export type ChatChannelKind = z.infer<typeof chatChannelKind>;

export const PUSH_PLATFORMS = ['ios', 'android', 'web'] as const;
export const pushPlatform = z.enum(PUSH_PLATFORMS);

// ---------------------------------------------------------------------------
// Project
// ---------------------------------------------------------------------------

export const createProjectInput = z.object({
  name: nonEmpty(200),
  type: projectType.default('other'),
  status: projectStatus.default('planning'),
  clientContactId: id.optional().nullable(),
  companyId: id.optional().nullable(),
  dealId: id.optional().nullable(),
  pmId: id.optional().nullable(),
  projectTemplateId: id.optional().nullable(),
  address: addressSchema.optional().nullable(),
  description: optionalText(),
  startDate: dateOnly.optional().nullable(),
  targetEndDate: dateOnly.optional().nullable(),
  contractValueCents: nonNegativeCents.default(0),
  budgetCents: nonNegativeCents.default(0),
  retainageBps: z.number().int().min(0).max(10_000).optional(),
  color: z.string().max(16).optional().nullable(),
  custom: customFieldValues.default({}),
});
export type CreateProjectInput = z.infer<typeof createProjectInput>;
export const updateProjectInput = patchOf(createProjectInput).extend({
  actualEndDate: dateOnly.optional().nullable(),
  percentComplete: z.number().int().min(0).max(100).optional(),
  /** Job-site point and radius for time-clock geofencing; set both coordinates or neither. */
  siteLat: z.number().min(-90).max(90).nullable().optional(),
  siteLng: z.number().min(-180).max(180).nullable().optional(),
  geofenceRadiusM: z.number().int().min(25).max(10_000).nullable().optional(),
});
export type UpdateProjectInput = z.infer<typeof updateProjectInput>;

export const projectSchema = baseRow.extend({
  number: z.string(),
  name: z.string(),
  type: projectType,
  status: projectStatus,
  clientContactId: id.nullable(),
  companyId: id.nullable(),
  dealId: id.nullable(),
  pmId: id.nullable(),
  address: addressSchema.nullable(),
  description: z.string().nullable(),
  startDate: z.string().nullable(),
  targetEndDate: z.string().nullable(),
  actualEndDate: z.string().nullable(),
  contractValueCents: cents,
  budgetCents: cents,
  retainageBps: z.number().int(),
  percentComplete: z.number().int(),
  color: z.string().nullable(),
  custom: customFieldValues,
});
export type Project = z.infer<typeof projectSchema>;

export const listProjectsInput = listInput.extend({
  status: projectStatus.optional(),
  statuses: z.array(projectStatus).optional(),
  pmId: id.optional(),
  clientContactId: id.optional(),
  mine: z.boolean().default(false),
});
export type ListProjectsInput = z.infer<typeof listProjectsInput>;

export const addProjectMemberInput = z.object({
  projectId: id,
  userId: id.optional().nullable(),
  contactId: id.optional().nullable(),
  role: memberRole,
  permissionTemplateId: id.optional().nullable(),
  overrides: permissionSet.optional().nullable(),
});
export type AddProjectMemberInput = z.infer<typeof addProjectMemberInput>;
export const updateProjectMemberInput = z.object({
  id,
  role: memberRole.optional(),
  permissionTemplateId: id.optional().nullable(),
  overrides: permissionSet.optional().nullable(),
});
export const projectMemberSchema = baseRow.extend({
  projectId: id,
  userId: id.nullable(),
  contactId: id.nullable(),
  role: memberRole,
  permissionTemplateId: id.nullable(),
  overrides: permissionSet.nullable(),
});
export type ProjectMember = z.infer<typeof projectMemberSchema>;

// ---------------------------------------------------------------------------
// Project templates
// ---------------------------------------------------------------------------

export const projectTemplatePhase = z.object({
  name: nonEmpty(120),
  durationDays: z.number().int().min(0).max(3650),
  offsetDays: z.number().int().min(0).max(3650).default(0),
  tasks: z.array(z.object({ name: nonEmpty(200), durationDays: z.number().int().min(0).max(3650) })).default([]),
});
export type ProjectTemplatePhase = z.infer<typeof projectTemplatePhase>;

export const createProjectTemplateInput = z.object({
  name: nonEmpty(120),
  description: optionalText(2000),
  businessType: businessType.optional().nullable(),
  projectType: projectType.default('other'),
  phases: z.array(projectTemplatePhase).max(100).default([]),
  costCodes: z.array(z.object({ code: z.string().max(40), name: z.string().max(200) })).max(1000).default([]),
  documentFolders: z.array(documentFolder).default([]),
  punchChecklist: z.array(z.string().max(300)).max(500).default([]),
});
export type CreateProjectTemplateInput = z.infer<typeof createProjectTemplateInput>;
export const updateProjectTemplateInput = patchOf(createProjectTemplateInput);
export const projectTemplateSchema = baseRow.extend({
  name: z.string(),
  description: z.string().nullable(),
  businessType: businessType.nullable(),
  projectType,
  phases: z.array(projectTemplatePhase),
  costCodes: z.array(z.object({ code: z.string(), name: z.string() })),
  documentFolders: z.array(documentFolder),
  punchChecklist: z.array(z.string()),
});
export type ProjectTemplate = z.infer<typeof projectTemplateSchema>;

// ---------------------------------------------------------------------------
// Schedule
// ---------------------------------------------------------------------------

export const createScheduleTaskInput = z.object({
  projectId: id,
  parentId: id.optional().nullable(),
  name: nonEmpty(200),
  startDate: dateOnly,
  endDate: dateOnly,
  status: scheduleTaskStatus.default('not_started'),
  percentComplete: z.number().int().min(0).max(100).default(0),
  assigneeUserId: id.optional().nullable(),
  assigneeCompanyId: id.optional().nullable(),
  dependencies: z.array(z.object({ taskId: id, type: dependencyType.default('FS'), lagDays: z.number().int().default(0) })).default([]),
  isMilestone: z.boolean().default(false),
  visibleToClient: z.boolean().default(true),
  position: z.number().int().default(0),
  notes: optionalText(5000),
});
export type CreateScheduleTaskInput = z.infer<typeof createScheduleTaskInput>;
export const updateScheduleTaskInput = patchOf(createScheduleTaskInput.omit({ projectId: true }));
export const scheduleTaskSchema = baseRow.extend({
  projectId: id,
  parentId: id.nullable(),
  name: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  status: scheduleTaskStatus,
  percentComplete: z.number().int(),
  assigneeUserId: id.nullable(),
  assigneeCompanyId: id.nullable(),
  dependencies: z.array(z.object({ taskId: id, type: dependencyType, lagDays: z.number().int() })),
  isMilestone: z.boolean(),
  visibleToClient: z.boolean(),
  position: z.number().int(),
  notes: z.string().nullable(),
});
export type ScheduleTask = z.infer<typeof scheduleTaskSchema>;

// ---------------------------------------------------------------------------
// Documents, drawings, markups
// ---------------------------------------------------------------------------

export const createDocumentInput = z.object({
  projectId: id.optional().nullable(),
  dealId: id.optional().nullable(),
  contactId: id.optional().nullable(),
  companyId: id.optional().nullable(),
  folder: documentFolder.default('general'),
  name: nonEmpty(255),
  r2Key: z.string().min(1).max(1024),
  contentType: z.string().max(255),
  sizeBytes: z.number().int().nonnegative(),
  sharedWithClient: z.boolean().default(false),
  description: optionalText(2000),
});
export type CreateDocumentInput = z.infer<typeof createDocumentInput>;
export const updateDocumentInput = patchOf(createDocumentInput.pick({ folder: true, name: true, sharedWithClient: true, description: true }));
export const documentSchema = baseRow.extend({
  projectId: id.nullable(),
  dealId: id.nullable(),
  contactId: id.nullable(),
  companyId: id.nullable(),
  folder: documentFolder,
  name: z.string(),
  r2Key: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().int(),
  sharedWithClient: z.boolean(),
  description: z.string().nullable(),
  aiTags: z.array(z.string()),
  extractedText: z.string().nullable(),
});
export type DocumentRecord = z.infer<typeof documentSchema>;
export const listDocumentsInput = listInput.extend({
  projectId: id.optional(),
  dealId: id.optional(),
  folder: documentFolder.optional(),
  sharedWithClient: z.boolean().optional(),
});

export const requestUploadInput = z.object({
  name: nonEmpty(255),
  contentType: z.string().max(255),
  sizeBytes: z.number().int().positive().max(2 * 1024 * 1024 * 1024),
  purpose: z.enum(['document', 'drawing', 'photo', 'logo', 'import', 'attachment']),
  projectId: id.optional().nullable(),
});
export type RequestUploadInput = z.infer<typeof requestUploadInput>;
export const requestUploadOutput = z.object({ key: z.string(), uploadUrl: z.string(), expiresAt: z.date() });

export const createDrawingInput = z.object({
  projectId: id,
  number: nonEmpty(40),
  title: nonEmpty(200),
  discipline: drawingDiscipline.default('architectural'),
  setName: z.string().trim().max(120).optional().nullable(),
});
export type CreateDrawingInput = z.infer<typeof createDrawingInput>;
export const updateDrawingInput = patchOf(createDrawingInput.omit({ projectId: true }));
export const drawingSchema = baseRow.extend({
  projectId: id,
  number: z.string(),
  title: z.string(),
  discipline: drawingDiscipline,
  setName: z.string().nullable(),
  currentRevisionId: id.nullable(),
});
export type Drawing = z.infer<typeof drawingSchema>;

export const createDrawingRevisionInput = z.object({
  drawingId: id,
  revision: nonEmpty(20),
  r2Key: z.string().min(1).max(1024),
  pageNumber: z.number().int().min(1).default(1),
  issuedAt: dateOnly.optional().nullable(),
  notes: optionalText(2000),
});
export type CreateDrawingRevisionInput = z.infer<typeof createDrawingRevisionInput>;
export const drawingRevisionSchema = baseRow.extend({
  drawingId: id,
  revision: z.string(),
  r2Key: z.string(),
  thumbnailKey: z.string().nullable(),
  pageNumber: z.number().int(),
  widthPx: z.number().int().nullable(),
  heightPx: z.number().int().nullable(),
  issuedAt: z.string().nullable(),
  notes: z.string().nullable(),
});
export type DrawingRevision = z.infer<typeof drawingRevisionSchema>;

export const markupShape = z.object({
  kind: markupKind,
  points: z.array(z.tuple([z.number(), z.number()])).max(10_000),
  color: z.string().max(16).default('#ef4444'),
  strokeWidth: z.number().min(0.5).max(50).default(2),
  text: z.string().max(2000).optional(),
});
export const createMarkupInput = z.object({
  drawingRevisionId: id,
  shapes: z.array(markupShape).max(5000),
  visibility: z.enum(['private', 'team', 'published']).default('team'),
  linkedEntityType: z.enum(['rfi', 'punch_item', 'photo', 'submittal']).optional().nullable(),
  linkedEntityId: id.optional().nullable(),
});
export type CreateMarkupInput = z.infer<typeof createMarkupInput>;
export const updateMarkupInput = patchOf(createMarkupInput.omit({ drawingRevisionId: true }));
export const markupSchema = baseRow.extend({
  drawingRevisionId: id,
  shapes: z.array(markupShape),
  visibility: z.enum(['private', 'team', 'published']),
  linkedEntityType: z.string().nullable(),
  linkedEntityId: id.nullable(),
  /** 1 = legacy `markupShape`, 2 = `sheetMarkupShape` (normalized sheet coordinates). */
  shapeVersion: z.number().int(),
});
export type Markup = z.infer<typeof markupSchema>;

// ---------------------------------------------------------------------------
// RFIs + submittals
// ---------------------------------------------------------------------------

export const createRfiInput = z.object({
  projectId: id,
  subject: nonEmpty(300),
  question: z.string().trim().min(1).max(20_000),
  status: rfiStatus.default('open'),
  priority: priority.default('normal'),
  dueDate: dateOnly.optional().nullable(),
  assignedToContactId: id.optional().nullable(),
  assignedToUserId: id.optional().nullable(),
  drawingId: id.optional().nullable(),
  specSection: z.string().trim().max(40).optional().nullable(),
  costImpact: z.boolean().optional().nullable(),
  scheduleImpact: z.boolean().optional().nullable(),
  custom: customFieldValues.default({}),
});
export type CreateRfiInput = z.infer<typeof createRfiInput>;
export const updateRfiInput = patchOf(createRfiInput.omit({ projectId: true })).extend({ answer: optionalText(20_000) });
export const rfiSchema = baseRow.extend({
  projectId: id,
  number: z.number().int(),
  subject: z.string(),
  question: z.string(),
  answer: z.string().nullable(),
  status: rfiStatus,
  priority,
  dueDate: z.string().nullable(),
  assignedToContactId: id.nullable(),
  assignedToUserId: id.nullable(),
  drawingId: id.nullable(),
  specSection: z.string().nullable(),
  costImpact: z.boolean().nullable(),
  scheduleImpact: z.boolean().nullable(),
  answeredAt: z.date().nullable(),
  closedAt: z.date().nullable(),
  custom: customFieldValues,
});
export type Rfi = z.infer<typeof rfiSchema>;
export const listRfisInput = listInput.extend({ projectId: id, status: rfiStatus.optional() });

export const createSubmittalInput = z.object({
  projectId: id,
  title: nonEmpty(300),
  specSection: z.string().trim().max(40).optional().nullable(),
  type: z.enum(['shop_drawing', 'product_data', 'sample', 'mockup', 'certificate', 'warranty', 'other']).default('product_data'),
  status: submittalStatus.default('draft'),
  submittedByCompanyId: id.optional().nullable(),
  reviewerContactId: id.optional().nullable(),
  dueDate: dateOnly.optional().nullable(),
  description: optionalText(),
  custom: customFieldValues.default({}),
});
export type CreateSubmittalInput = z.infer<typeof createSubmittalInput>;
export const updateSubmittalInput = patchOf(createSubmittalInput.omit({ projectId: true })).extend({
  reviewNotes: optionalText(),
});
export const submittalSchema = baseRow.extend({
  projectId: id,
  number: z.number().int(),
  revision: z.number().int(),
  title: z.string(),
  specSection: z.string().nullable(),
  type: z.string(),
  status: submittalStatus,
  submittedByCompanyId: id.nullable(),
  reviewerContactId: id.nullable(),
  dueDate: z.string().nullable(),
  description: z.string().nullable(),
  reviewNotes: z.string().nullable(),
  submittedAt: z.date().nullable(),
  reviewedAt: z.date().nullable(),
  custom: customFieldValues,
});
export type Submittal = z.infer<typeof submittalSchema>;
export const listSubmittalsInput = listInput.extend({ projectId: id, status: submittalStatus.optional() });

// ---------------------------------------------------------------------------
// Daily logs, photos, punch items
// ---------------------------------------------------------------------------

export const weatherSchema = z.object({
  conditions: z.string().max(80).optional(),
  highF: z.number().optional(),
  lowF: z.number().optional(),
  precipitationIn: z.number().optional(),
  windMph: z.number().optional(),
  source: z.enum(['manual', 'api']).default('manual'),
});
export const manpowerEntry = z.object({
  companyId: id.optional().nullable(),
  trade: z.string().max(80),
  workers: z.number().int().min(0).max(10_000),
  hours: z.number().min(0).max(100_000),
});

export const createDailyLogInput = z.object({
  projectId: id,
  logDate: dateOnly,
  weather: weatherSchema.optional().nullable(),
  workPerformed: optionalText(50_000),
  manpower: z.array(manpowerEntry).max(200).default([]),
  delays: optionalText(10_000),
  safetyNotes: optionalText(10_000),
  visitors: optionalText(5000),
  deliveries: optionalText(5000),
  visibleToClient: z.boolean().default(false),
  custom: customFieldValues.default({}),
});
export type CreateDailyLogInput = z.infer<typeof createDailyLogInput>;
export const updateDailyLogInput = patchOf(createDailyLogInput.omit({ projectId: true }));
export const dailyLogSchema = baseRow.extend({
  projectId: id,
  logDate: z.string(),
  weather: weatherSchema.nullable(),
  workPerformed: z.string().nullable(),
  manpower: z.array(manpowerEntry),
  delays: z.string().nullable(),
  safetyNotes: z.string().nullable(),
  visitors: z.string().nullable(),
  deliveries: z.string().nullable(),
  visibleToClient: z.boolean(),
  submittedAt: z.date().nullable(),
  custom: customFieldValues,
  approvedAt: z.date().nullable(),
  approvedBy: id.nullable(),
  rejectedAt: z.date().nullable(),
  rejectedBy: id.nullable(),
  /** Reason given on the latest rejection (cleared when the log is resubmitted and approved). */
  rejectionReason: z.string().nullable(),
});
export type DailyLog = z.infer<typeof dailyLogSchema>;
export const listDailyLogsInput = listInput.extend({ projectId: id, from: dateOnly.optional(), to: dateOnly.optional() });

export const createPhotoInput = z.object({
  projectId: id,
  dailyLogId: id.optional().nullable(),
  punchItemId: id.optional().nullable(),
  r2Key: z.string().min(1).max(1024),
  caption: optionalText(2000),
  takenAt: isoDate.optional().nullable(),
  location: z.object({ lat: z.number(), lng: z.number(), accuracyM: z.number().optional() }).optional().nullable(),
  tags: z.array(z.string().max(60)).max(50).default([]),
  sharedWithClient: z.boolean().default(false),
});
export type CreatePhotoInput = z.infer<typeof createPhotoInput>;
export const updatePhotoInput = patchOf(createPhotoInput.pick({ caption: true, tags: true, sharedWithClient: true, dailyLogId: true, punchItemId: true }));
export const photoSchema = baseRow.extend({
  projectId: id,
  dailyLogId: id.nullable(),
  punchItemId: id.nullable(),
  r2Key: z.string(),
  thumbnailKey: z.string().nullable(),
  caption: z.string().nullable(),
  takenAt: z.date().nullable(),
  location: z.object({ lat: z.number(), lng: z.number(), accuracyM: z.number().optional() }).nullable(),
  tags: z.array(z.string()),
  aiTags: z.array(z.string()),
  sharedWithClient: z.boolean(),
  albumId: id.nullable(),
});
export type Photo = z.infer<typeof photoSchema>;
export const listPhotosInput = listInput.extend({ projectId: id, dailyLogId: id.optional(), sharedWithClient: z.boolean().optional() });

export const createPunchItemInput = z.object({
  projectId: id,
  title: nonEmpty(300),
  description: optionalText(10_000),
  status: punchStatus.default('open'),
  priority: priority.default('normal'),
  location: z.string().trim().max(200).optional().nullable(),
  trade: z.string().trim().max(80).optional().nullable(),
  assigneeCompanyId: id.optional().nullable(),
  assigneeUserId: id.optional().nullable(),
  dueDate: dateOnly.optional().nullable(),
  drawingId: id.optional().nullable(),
  pin: z.object({ x: z.number(), y: z.number() }).optional().nullable(),
  visibleToClient: z.boolean().default(false),
  custom: customFieldValues.default({}),
});
export type CreatePunchItemInput = z.infer<typeof createPunchItemInput>;
export const updatePunchItemInput = patchOf(createPunchItemInput.omit({ projectId: true }));
export const punchItemSchema = baseRow.extend({
  projectId: id,
  number: z.number().int(),
  title: z.string(),
  description: z.string().nullable(),
  status: punchStatus,
  priority,
  location: z.string().nullable(),
  trade: z.string().nullable(),
  assigneeCompanyId: id.nullable(),
  assigneeUserId: id.nullable(),
  dueDate: z.string().nullable(),
  drawingId: id.nullable(),
  pin: z.object({ x: z.number(), y: z.number() }).nullable(),
  visibleToClient: z.boolean(),
  closedAt: z.date().nullable(),
  custom: customFieldValues,
});
export type PunchItem = z.infer<typeof punchItemSchema>;
export const listPunchItemsInput = listInput.extend({ projectId: id, status: punchStatus.optional(), assigneeCompanyId: id.optional() });

// ---------------------------------------------------------------------------
// Bid management
// ---------------------------------------------------------------------------

export const createBidPackageInput = z.object({
  projectId: id.optional().nullable(),
  dealId: id.optional().nullable(),
  name: nonEmpty(200),
  trade: z.string().trim().max(80).optional().nullable(),
  scope: optionalText(50_000),
  dueAt: isoDate.optional().nullable(),
  status: bidPackageStatus.default('draft'),
  budgetCents: nonNegativeCents.optional().nullable(),
  documentIds: z.array(id).max(200).default([]),
});
export type CreateBidPackageInput = z.infer<typeof createBidPackageInput>;
export const updateBidPackageInput = patchOf(createBidPackageInput);
export const bidPackageSchema = baseRow.extend({
  projectId: id.nullable(),
  dealId: id.nullable(),
  name: z.string(),
  trade: z.string().nullable(),
  scope: z.string().nullable(),
  dueAt: z.date().nullable(),
  status: bidPackageStatus,
  budgetCents: cents.nullable(),
  documentIds: z.array(id),
  awardedBidId: id.nullable(),
});
export type BidPackage = z.infer<typeof bidPackageSchema>;

export const createBidInput = z.object({
  bidPackageId: id,
  companyId: id,
  contactId: id.optional().nullable(),
  status: bidStatus.default('invited'),
  amountCents: nonNegativeCents.optional().nullable(),
  inclusions: optionalText(20_000),
  exclusions: optionalText(20_000),
  notes: optionalText(20_000),
  documentIds: z.array(id).max(50).default([]),
});
export type CreateBidInput = z.infer<typeof createBidInput>;
export const updateBidInput = patchOf(createBidInput.omit({ bidPackageId: true, companyId: true }));
export const bidSchema = baseRow.extend({
  bidPackageId: id,
  companyId: id,
  contactId: id.nullable(),
  status: bidStatus,
  amountCents: cents.nullable(),
  inclusions: z.string().nullable(),
  exclusions: z.string().nullable(),
  notes: z.string().nullable(),
  documentIds: z.array(id),
  invitedAt: z.date().nullable(),
  submittedAt: z.date().nullable(),
});
export type Bid = z.infer<typeof bidSchema>;

// ===========================================================================
// Projects module: additional procedure inputs (added by the projects module).
// Existing exports above are unchanged.
// ===========================================================================

export const projectIdInput = z.object({ projectId: id });
export type ProjectIdInput = z.infer<typeof projectIdInput>;

export const archiveInput = z.object({ id, archived: z.boolean().default(true) });
export type ArchiveInput = z.infer<typeof archiveInput>;

// ---------------------------------------------------------------------------
// Team / directory
// ---------------------------------------------------------------------------

export const DIRECTORY_KINDS = ['team', 'client', 'subcontractor', 'supplier', 'consultant', 'other'] as const;
export const directoryKind = z.enum(DIRECTORY_KINDS);
export type DirectoryKind = z.infer<typeof directoryKind>;

// ---------------------------------------------------------------------------
// Files: document versions
// ---------------------------------------------------------------------------

export const addDocumentVersionInput = z.object({
  documentId: id,
  r2Key: z.string().min(1).max(1024),
  name: nonEmpty(255).optional(),
  contentType: z.string().max(255),
  sizeBytes: z.number().int().nonnegative(),
  notes: optionalText(2000),
});
export type AddDocumentVersionInput = z.infer<typeof addDocumentVersionInput>;

export const documentVersionSchema = z.object({
  version: z.number().int(),
  r2Key: z.string(),
  name: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().int(),
  notes: z.string().nullable(),
  uploadedBy: id.nullable(),
  uploadedAt: z.date(),
  current: z.boolean(),
});
export type DocumentVersion = z.infer<typeof documentVersionSchema>;

export const fileUrlInput = z.object({ id, version: z.number().int().positive().optional() });

// ---------------------------------------------------------------------------
// Drawings
// ---------------------------------------------------------------------------

export const listDrawingsInput = z.object({
  projectId: id,
  setName: z.string().trim().max(120).optional(),
  discipline: drawingDiscipline.optional(),
  search: z.string().trim().max(200).optional(),
  includeArchived: z.boolean().default(false),
});

export const createDrawingFullInput = createDrawingInput.extend({
  /** Optional first revision (the uploaded sheet). */
  revision: createDrawingRevisionInput.omit({ drawingId: true }).optional(),
});
export type CreateDrawingFullInput = z.infer<typeof createDrawingFullInput>;

/** Upload a multi-page PDF as a sheet set: each page becomes a drawing sharing the file. */
export const createSheetSetInput = z.object({
  projectId: id,
  setName: nonEmpty(120),
  discipline: drawingDiscipline.default('architectural'),
  revision: nonEmpty(20).default('0'),
  issuedAt: dateOnly.optional().nullable(),
  r2Key: z.string().min(1).max(1024),
  sheets: z
    .array(z.object({ number: nonEmpty(40), title: nonEmpty(200), pageNumber: z.number().int().min(1), discipline: drawingDiscipline.optional() }))
    .min(1)
    .max(500),
});
export type CreateSheetSetInput = z.infer<typeof createSheetSetInput>;

/** Pin an RFI or punch item to a sheet. Coordinates are normalised to the page (0..1). */
export const PIN_ENTITY_TYPES = ['rfi', 'punch_item'] as const;
export const pinEntityType = z.enum(PIN_ENTITY_TYPES);
export type PinEntityType = z.infer<typeof pinEntityType>;
export const pinToSheetInput = z.object({
  drawingRevisionId: id,
  entityType: pinEntityType,
  entityId: id,
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
});
export type PinToSheetInput = z.infer<typeof pinToSheetInput>;

// ---------------------------------------------------------------------------
// RFIs: distribution, responses, workflow
// ---------------------------------------------------------------------------

export const rfiDistributionFields = {
  distributionUserIds: z.array(id).max(100).optional(),
  distributionContactIds: z.array(id).max(100).optional(),
};
export const createRfiFullInput = createRfiInput.extend(rfiDistributionFields);
export type CreateRfiFullInput = z.infer<typeof createRfiFullInput>;
export const updateRfiFullInput = updateRfiInput.extend(rfiDistributionFields);
export type UpdateRfiFullInput = z.infer<typeof updateRfiFullInput>;

export const RFI_ACTIONS = ['open', 'answer', 'close', 'reopen', 'void'] as const;
export const rfiAction = z.enum(RFI_ACTIONS);
export type RfiAction = z.infer<typeof rfiAction>;
export const rfiTransitionInput = z.object({
  id,
  action: rfiAction,
  /** Official answer, required for `answer`. */
  answer: optionalText(20_000),
  expectedVersion: z.number().int().nonnegative().optional(),
});

export const addRfiResponseInput = z.object({
  rfiId: id,
  body: z.string().trim().min(1).max(20_000),
  /** Marks this response as the official answer (also moves the RFI to `answered`). */
  official: z.boolean().default(false),
  attachments: z
    .array(z.object({ key: z.string().min(1).max(1024), name: nonEmpty(255), contentType: z.string().max(255), size: z.number().int().nonnegative() }))
    .max(20)
    .default([]),
});
export type AddRfiResponseInput = z.infer<typeof addRfiResponseInput>;

export const listRfisFullInput = listRfisInput.extend({
  ballInCourt: z.enum(['me', 'any']).default('any'),
  overdueOnly: z.boolean().default(false),
});

// ---------------------------------------------------------------------------
// Submittals
// ---------------------------------------------------------------------------

export const SUBMITTAL_ACTIONS = ['submit', 'start_review', 'review', 'close', 'reopen'] as const;
export const submittalAction = z.enum(SUBMITTAL_ACTIONS);
export type SubmittalAction = z.infer<typeof submittalAction>;
export const SUBMITTAL_REVIEW_OUTCOMES = ['approved', 'approved_as_noted', 'revise_resubmit', 'rejected'] as const;
export const submittalReviewOutcome = z.enum(SUBMITTAL_REVIEW_OUTCOMES);
export type SubmittalReviewOutcome = z.infer<typeof submittalReviewOutcome>;
export const submittalTransitionInput = z.object({
  id,
  action: submittalAction,
  outcome: submittalReviewOutcome.optional(),
  reviewNotes: optionalText(),
  expectedVersion: z.number().int().nonnegative().optional(),
});

// ---------------------------------------------------------------------------
// Daily logs
// ---------------------------------------------------------------------------

export const DAILY_LOG_STATES = ['draft', 'submitted', 'approved'] as const;
export const dailyLogState = z.enum(DAILY_LOG_STATES);
export type DailyLogState = z.infer<typeof dailyLogState>;
export const DAILY_LOG_ACTIONS = ['submit', 'approve', 'reject'] as const;
export const dailyLogAction = z.enum(DAILY_LOG_ACTIONS);
export type DailyLogAction = z.infer<typeof dailyLogAction>;
export const dailyLogTransitionInput = z.object({
  id,
  action: dailyLogAction,
  reason: optionalText(2000),
});

// ---------------------------------------------------------------------------
// Photos (albums are `photo_album` rows; `album` filters by album name)
// ---------------------------------------------------------------------------

export const listPhotosFullInput = listPhotosInput.extend({
  album: z.string().trim().max(60).optional(),
  tag: z.string().trim().max(60).optional(),
  punchItemId: id.optional(),
});
export const setPhotoAlbumInput = z.object({
  photoIds: z.array(id).min(1).max(500),
  album: z.string().trim().min(1).max(50).nullable(),
});

// ---------------------------------------------------------------------------
// Punch list
// ---------------------------------------------------------------------------

export const PUNCH_ACTIONS = ['start', 'ready_for_review', 'close', 'reopen', 'void'] as const;
export const punchAction = z.enum(PUNCH_ACTIONS);
export type PunchAction = z.infer<typeof punchAction>;
export const punchTransitionInput = z.object({ id, action: punchAction, note: optionalText(2000) });
export const listPunchItemsFullInput = listPunchItemsInput.extend({
  drawingId: id.optional(),
  mine: z.boolean().default(false),
});

// ---------------------------------------------------------------------------
// Meetings with minutes (`meeting`, `meeting_attendee`, `meeting_action_item`)
// ---------------------------------------------------------------------------

export const MEETING_STATUSES = ['scheduled', 'minutes_draft', 'minutes_published', 'cancelled'] as const;
export const meetingStatus = z.enum(MEETING_STATUSES);
export type MeetingStatus = z.infer<typeof meetingStatus>;
export const MEETING_TYPES = ['oac', 'subcontractor', 'safety', 'preconstruction', 'coordination', 'internal', 'other'] as const;
export const meetingType = z.enum(MEETING_TYPES);
export type MeetingType = z.infer<typeof meetingType>;

export const meetingAttendee = z.object({
  userId: id.optional().nullable(),
  contactId: id.optional().nullable(),
  name: nonEmpty(200),
  company: z.string().trim().max(200).optional().nullable(),
  present: z.boolean().default(true),
});
export type MeetingAttendee = z.infer<typeof meetingAttendee>;
export const meetingItem = z.object({
  id: z.string().min(1).max(64),
  topic: nonEmpty(300),
  notes: z.string().max(10_000).default(''),
  ownerName: z.string().max(200).optional().nullable(),
  dueDate: dateOnly.optional().nullable(),
  status: z.enum(['open', 'closed', 'info']).default('open'),
});
export type MeetingItem = z.infer<typeof meetingItem>;

export const createMeetingInput = z.object({
  projectId: id,
  title: nonEmpty(300),
  type: meetingType.default('coordination'),
  startsAt: isoDate,
  durationMinutes: z.number().int().min(5).max(24 * 60).default(60),
  location: z.string().trim().max(300).optional().nullable(),
  agenda: z.string().max(20_000).optional().nullable(),
  attendees: z.array(meetingAttendee).max(200).default([]),
});
export type CreateMeetingInput = z.infer<typeof createMeetingInput>;
export const updateMeetingInput = patchOf(createMeetingInput.omit({ projectId: true })).extend({
  minutes: z.string().max(50_000).optional().nullable(),
  items: z.array(meetingItem).max(300).optional(),
  status: meetingStatus.optional(),
});
export type UpdateMeetingInput = z.infer<typeof updateMeetingInput>;

// ---------------------------------------------------------------------------
// Bid management
// ---------------------------------------------------------------------------

export const listBidPackagesInput = z.object({
  projectId: id.optional(),
  dealId: id.optional(),
  status: bidPackageStatus.optional(),
  includeArchived: z.boolean().default(false),
});
export const findBiddersInput = z.object({
  bidPackageId: id,
  trade: z.string().trim().max(80).optional(),
  region: z.string().trim().max(120).optional(),
  city: z.string().trim().max(120).optional(),
  search: z.string().trim().max(200).optional(),
  prequalifiedOnly: z.boolean().default(false),
});
export const inviteBiddersInput = z.object({
  bidPackageId: id,
  invites: z.array(z.object({ companyId: id, contactId: id.optional().nullable() })).min(1).max(100),
  message: optionalText(5000),
  sendEmail: z.boolean().default(true),
});
export const submitBidInput = z.object({
  id,
  amountCents: nonNegativeCents,
  inclusions: optionalText(20_000),
  exclusions: optionalText(20_000),
  notes: optionalText(20_000),
  documentIds: z.array(id).max(50).optional(),
});
export const bidResponseInput = z.object({ id, action: z.enum(['decline', 'withdraw', 'mark_viewed']) });
export const awardBidInput = z.object({ bidPackageId: id, bidId: id, notifyBidders: z.boolean().default(true) });
export const bidPackageTransitionInput = z.object({ id, action: z.enum(['open', 'start_leveling', 'cancel', 'reopen']) });

// ---------------------------------------------------------------------------
// Schedule (lookahead + Gantt)
// ---------------------------------------------------------------------------

export const listScheduleInput = z.object({
  projectId: id,
  from: dateOnly.optional(),
  to: dateOnly.optional(),
  includeArchived: z.boolean().default(false),
});
export const lookaheadInput = z.object({ projectId: id, start: dateOnly.optional(), weeks: z.number().int().min(1).max(12).default(3) });
export const updateScheduleTaskFullInput = updateScheduleTaskInput.extend({
  /** Shift dependent tasks forward when this task's dates move. */
  cascade: z.boolean().default(true),
});
export const reorderScheduleInput = z.object({ projectId: id, orderedIds: z.array(id).max(2000) });

// ===========================================================================
// Dedicated project tables (migration 0003). Row schemas mirror the Drizzle tables in
// packages/db/src/schema/projects.ts.
// ===========================================================================

// ---------------------------------------------------------------------------
// Document versions (`document_version`)
// ---------------------------------------------------------------------------

/**
 * One uploaded file version of a `document`. `document.r2_key`/`name`/`content_type`/`size_bytes`
 * always mirror the highest `versionNumber`; older versions stay downloadable.
 */
export const documentVersionRecordSchema = baseRow.extend({
  documentId: id,
  versionNumber: z.number().int(),
  r2Key: z.string(),
  name: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().int(),
  /** SHA-256 hex of the file when the uploader computed it (dedupe / integrity). */
  checksum: z.string().nullable(),
  notes: z.string().nullable(),
});
export type DocumentVersionRecord = z.infer<typeof documentVersionRecordSchema>;

// ---------------------------------------------------------------------------
// Photo albums (`photo_album`, `photo.album_id`)
// ---------------------------------------------------------------------------

export const createPhotoAlbumInput = z.object({
  projectId: id,
  name: nonEmpty(120),
  description: optionalText(2000),
  sharedWithClient: z.boolean().default(false),
  position: z.number().int().min(0).default(0),
});
export type CreatePhotoAlbumInput = z.infer<typeof createPhotoAlbumInput>;
export const updatePhotoAlbumInput = patchOf(createPhotoAlbumInput.omit({ projectId: true })).extend({
  coverPhotoId: id.optional().nullable(),
});
export type UpdatePhotoAlbumInput = z.infer<typeof updatePhotoAlbumInput>;
export const photoAlbumSchema = baseRow.extend({
  projectId: id,
  name: z.string(),
  description: z.string().nullable(),
  coverPhotoId: id.nullable(),
  sharedWithClient: z.boolean(),
  position: z.number().int(),
});
export type PhotoAlbum = z.infer<typeof photoAlbumSchema>;
export const movePhotosToAlbumInput = z.object({
  photoIds: z.array(id).min(1).max(500),
  albumId: id.nullable(),
});

// ---------------------------------------------------------------------------
// Meetings (`meeting`, `meeting_attendee`, `meeting_action_item`)
// ---------------------------------------------------------------------------

export const MEETING_ITEM_STATUSES = ['open', 'closed', 'info'] as const;
export const meetingItemStatus = z.enum(MEETING_ITEM_STATUSES);
export type MeetingItemStatus = z.infer<typeof meetingItemStatus>;

export const meetingRecordSchema = baseRow.extend({
  projectId: id,
  /** Sequential per project. */
  number: z.number().int(),
  title: z.string(),
  type: meetingType,
  status: meetingStatus,
  startsAt: z.date(),
  durationMinutes: z.number().int(),
  location: z.string().nullable(),
  agenda: z.string().nullable(),
  minutes: z.string().nullable(),
  /** Previous meeting in the series; its open action items carry forward. */
  previousMeetingId: id.nullable(),
  visibleToClient: z.boolean(),
  publishedAt: z.date().nullable(),
  publishedBy: id.nullable(),
  custom: customFieldValues,
});
export type MeetingRecord = z.infer<typeof meetingRecordSchema>;

export const meetingAttendeeRecordSchema = baseRow.extend({
  meetingId: id,
  userId: id.nullable(),
  contactId: id.nullable(),
  companyId: id.nullable(),
  name: z.string(),
  email: z.string().nullable(),
  companyName: z.string().nullable(),
  /** Null until attendance is taken. */
  present: z.boolean().nullable(),
  position: z.number().int(),
});
export type MeetingAttendeeRecord = z.infer<typeof meetingAttendeeRecordSchema>;

export const meetingActionItemRecordSchema = baseRow.extend({
  meetingId: id,
  projectId: id,
  position: z.number().int(),
  topic: z.string(),
  notes: z.string(),
  status: meetingItemStatus,
  ownerUserId: id.nullable(),
  ownerContactId: id.nullable(),
  ownerName: z.string().nullable(),
  dueDate: z.string().nullable(),
  closedAt: z.date().nullable(),
  /** Item of an earlier meeting this one carries forward. */
  carriedFromItemId: id.nullable(),
  /** CRM task created for the owner, if any. */
  taskId: id.nullable(),
});
export type MeetingActionItemRecord = z.infer<typeof meetingActionItemRecordSchema>;

export const upsertMeetingActionItemInput = z.object({
  id: id.optional(),
  meetingId: id,
  position: z.number().int().min(0).default(0),
  topic: nonEmpty(300),
  notes: z.string().max(10_000).default(''),
  status: meetingItemStatus.default('open'),
  ownerUserId: id.optional().nullable(),
  ownerContactId: id.optional().nullable(),
  ownerName: z.string().trim().max(200).optional().nullable(),
  dueDate: dateOnly.optional().nullable(),
  createTask: z.boolean().default(false),
  expectedVersion: z.number().int().nonnegative().optional(),
});
export type UpsertMeetingActionItemInput = z.infer<typeof upsertMeetingActionItemInput>;

// ---------------------------------------------------------------------------
// RFI responses + distribution (`rfi_response`, `rfi_distribution`)
// ---------------------------------------------------------------------------

export const rfiAttachmentSchema = z.object({
  key: z.string().min(1).max(1024),
  name: nonEmpty(255),
  contentType: z.string().max(255),
  size: z.number().int().nonnegative(),
});
export type RfiAttachment = z.infer<typeof rfiAttachmentSchema>;

export const rfiResponseRecordSchema = baseRow.extend({
  rfiId: id,
  body: z.string(),
  /** The official answer (copied to `rfi.answer`). At most one non-archived official response per RFI. */
  official: z.boolean(),
  authorUserId: id.nullable(),
  authorContactId: id.nullable(),
  attachments: z.array(rfiAttachmentSchema),
});
export type RfiResponseRecord = z.infer<typeof rfiResponseRecordSchema>;

/** Exactly one of `userId` / `contactId` is set (CHECK constraint). */
export const rfiDistributionRecordSchema = baseRow.extend({
  rfiId: id,
  userId: id.nullable(),
  contactId: id.nullable(),
  notifiedAt: z.date().nullable(),
});
export type RfiDistributionRecord = z.infer<typeof rfiDistributionRecordSchema>;

// ---------------------------------------------------------------------------
// Sheet markup coordinate convention (see docs/CONVENTIONS.md, "Drawing markups")
// ---------------------------------------------------------------------------
//
// Every markup geometry is stored in NORMALIZED SHEET COORDINATES:
//   - x is a fraction of the sheet width, y a fraction of the sheet height, both 0..1;
//   - origin at the TOP-LEFT corner, x grows right, y grows down;
//   - "the sheet" is the page as displayed upright, i.e. with the PDF page's intrinsic
//     /Rotate already applied (the same orientation as the thumbnail and `width_px`/`height_px`);
//   - a viewer rotation chosen by the user is presentation only. Convert pointer positions with
//     `viewToSheet(point, viewRotation)` and back with `sheetToView` (`@buildbee/domain/projects`),
//     so the stored data never depends on how the sheet was being viewed.
// Lengths that are not positions (stroke width, font size) are fractions of the sheet's SHORTER
// side, so they look the same in portrait and landscape and under any view rotation.
// A shape's own `rotation` is degrees clockwise about the centre of its box, applied in physical
// (aspect-correct) space: convert with the sheet aspect ratio (width / height) before rotating.

export const SHEET_ROTATIONS = [0, 90, 180, 270] as const;
export const sheetRotation = z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]);
export type SheetRotation = z.infer<typeof sheetRotation>;

/** One normalized coordinate (0..1). */
export const sheetCoord = z.number().min(0).max(1);
export const sheetPoint = z.object({ x: sheetCoord, y: sheetCoord });
export type SheetPoint = z.infer<typeof sheetPoint>;

/** Axis-aligned box before the shape's own rotation: top-left corner plus size, all normalized. */
export const sheetBox = z
  .object({
    x: sheetCoord,
    y: sheetCoord,
    width: z.number().min(0).max(1),
    height: z.number().min(0).max(1),
  })
  .refine((b) => b.x + b.width <= 1 + 1e-9 && b.y + b.height <= 1 + 1e-9, { message: 'Box must stay inside the sheet' });
export type SheetBox = z.infer<typeof sheetBox>;

const sheetLength = z.number().min(0).max(0.25);
const shapeRotation = z.number().min(-360).max(360).default(0);

export const sheetMarkupStyle = z.object({
  stroke: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Expected #RRGGBB').default('#ef4444'),
  /** Fraction of the sheet's shorter side (0.002 = 0.2%). */
  strokeWidth: sheetLength.default(0.002),
  fill: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Expected #RRGGBB').nullable().default(null),
  opacity: z.number().min(0).max(1).default(1),
  dash: z.enum(['solid', 'dashed', 'dotted']).default('solid'),
});
export type SheetMarkupStyle = z.infer<typeof sheetMarkupStyle>;

export const SHEET_MARKUP_TYPES = ['line', 'arrow', 'rect', 'ellipse', 'polyline', 'freehand', 'text', 'cloud', 'pin'] as const;
export const sheetMarkupType = z.enum(SHEET_MARKUP_TYPES);
export type SheetMarkupType = z.infer<typeof sheetMarkupType>;

const shapeBase = {
  /** Client-generated id (stable across edits and offline sync). */
  id: z.string().min(1).max(64),
  style: sheetMarkupStyle.default({ stroke: '#ef4444', strokeWidth: 0.002, fill: null, opacity: 1, dash: 'solid' }),
  /** Optional label shown on hover / in the markup list. */
  label: z.string().max(500).optional(),
};

export const sheetLineShape = z.object({ ...shapeBase, type: z.literal('line'), from: sheetPoint, to: sheetPoint });
export const sheetArrowShape = z.object({
  ...shapeBase,
  type: z.literal('arrow'),
  /** Tail. */
  from: sheetPoint,
  /** Head. */
  to: sheetPoint,
  heads: z.enum(['end', 'both']).default('end'),
});
export const sheetRectShape = z.object({ ...shapeBase, type: z.literal('rect'), box: sheetBox, rotation: shapeRotation });
export const sheetEllipseShape = z.object({ ...shapeBase, type: z.literal('ellipse'), box: sheetBox, rotation: shapeRotation });
export const sheetPolylineShape = z.object({
  ...shapeBase,
  type: z.literal('polyline'),
  points: z.array(sheetPoint).min(2).max(5000),
  /** Closed polylines are polygons (fill applies). */
  closed: z.boolean().default(false),
});
export const sheetFreehandShape = z.object({
  ...shapeBase,
  type: z.literal('freehand'),
  /** Raw ink samples in drawing order; renderers smooth them. */
  points: z.array(sheetPoint).min(2).max(10_000),
});
export const sheetTextShape = z.object({
  ...shapeBase,
  type: z.literal('text'),
  /** Top-left of the text box. */
  at: sheetPoint,
  /** Wrap width (fraction of sheet width); null = no wrapping. */
  width: z.number().min(0).max(1).nullable().default(null),
  text: z.string().min(1).max(5000),
  /** Fraction of the sheet's shorter side. */
  fontSize: sheetLength.default(0.012),
  align: z.enum(['left', 'center', 'right']).default('left'),
  rotation: shapeRotation,
});
export const sheetCloudShape = z.object({
  ...shapeBase,
  type: z.literal('cloud'),
  /** Closed outline of the revision cloud (a rectangle cloud is four points). */
  points: z.array(sheetPoint).min(3).max(2000),
  /** Arc radius as a fraction of the sheet's shorter side. */
  arcSize: sheetLength.default(0.01),
});
export const sheetPinShape = z.object({
  ...shapeBase,
  type: z.literal('pin'),
  /** The pin's tip. */
  at: sheetPoint,
  /** Linked record (RFI, punch item, photo, submittal) the pin opens. */
  entityType: z.enum(['rfi', 'punch_item', 'photo', 'submittal']).optional(),
  entityId: id.optional(),
});

export const sheetMarkupShape = z.discriminatedUnion('type', [
  sheetLineShape,
  sheetArrowShape,
  sheetRectShape,
  sheetEllipseShape,
  sheetPolylineShape,
  sheetFreehandShape,
  sheetTextShape,
  sheetCloudShape,
  sheetPinShape,
]);
export type SheetMarkupShape = z.infer<typeof sheetMarkupShape>;
export type SheetMarkupShapeInput = z.input<typeof sheetMarkupShape>;

/**
 * `markup.shape_version`: 1 = legacy `markupShape` (`{ kind, points: [x, y][] }`),
 * 2 = `sheetMarkupShape` above. New writes use 2.
 */
export const MARKUP_SHAPE_VERSIONS = [1, 2] as const;
export const CURRENT_MARKUP_SHAPE_VERSION = 2 as const;

export const createSheetMarkupInput = z.object({
  drawingRevisionId: id,
  shapes: z.array(sheetMarkupShape).max(5000),
  visibility: z.enum(['private', 'team', 'published']).default('team'),
  linkedEntityType: z.enum(['rfi', 'punch_item', 'photo', 'submittal']).optional().nullable(),
  linkedEntityId: id.optional().nullable(),
});
export type CreateSheetMarkupInput = z.infer<typeof createSheetMarkupInput>;
export const updateSheetMarkupInput = patchOf(createSheetMarkupInput.omit({ drawingRevisionId: true }));
export type UpdateSheetMarkupInput = z.infer<typeof updateSheetMarkupInput>;

// ===========================================================================
// Projects module procedure inputs for the dedicated tables (additive).
// ===========================================================================

/** SHA-256 hex digest of an uploaded file. */
export const fileChecksum = z.string().regex(/^[0-9a-f]{64}$/, 'Expected a SHA-256 hex digest');

// Document versions ----------------------------------------------------------

export const addDocumentVersionFullInput = addDocumentVersionInput.extend({ checksum: fileChecksum.optional().nullable() });
export type AddDocumentVersionFullInput = z.infer<typeof addDocumentVersionFullInput>;
export const createDocumentFullInput = createDocumentInput.extend({ checksum: fileChecksum.optional().nullable() });
export type CreateDocumentFullInput = z.infer<typeof createDocumentFullInput>;
/** Makes an earlier version current again by adding it as a new version (history is never rewritten). */
export const restoreDocumentVersionInput = z.object({
  documentId: id,
  versionNumber: z.number().int().positive(),
  notes: optionalText(2000),
  expectedVersion,
});
export type RestoreDocumentVersionInput = z.infer<typeof restoreDocumentVersionInput>;

// Photos and albums ----------------------------------------------------------

export const createPhotoFullInput = createPhotoInput.extend({ albumId: id.optional().nullable() });
export type CreatePhotoFullInput = z.infer<typeof createPhotoFullInput>;
export const listPhotosInAlbumInput = listPhotosFullInput.extend({
  albumId: id.optional(),
  /** Only photos that are not in any album. */
  unfiled: z.boolean().optional(),
});
export type ListPhotosInAlbumInput = z.infer<typeof listPhotosInAlbumInput>;
export const reorderPhotoAlbumsInput = z.object({ projectId: id, orderedIds: z.array(id).max(500) });

// Meetings -------------------------------------------------------------------

export const meetingAttendeeInput = meetingAttendee.extend({
  companyId: id.optional().nullable(),
  email: z.string().trim().email().max(320).optional().nullable(),
  /** Null until attendance is taken. */
  present: z.boolean().optional().nullable(),
});
export type MeetingAttendeeInput = z.infer<typeof meetingAttendeeInput>;

/** An action item edited inline in the minutes. `id` is the row id when the item already exists. */
export const meetingItemInput = meetingItem.extend({
  ownerUserId: id.optional().nullable(),
  ownerContactId: id.optional().nullable(),
});
export type MeetingItemInput = z.infer<typeof meetingItemInput>;

export const createMeetingFullInput = createMeetingInput.extend({
  attendees: z.array(meetingAttendeeInput).max(200).default([]),
  /** Previous meeting of the series; its open action items are copied in when `carryForward` is set. */
  previousMeetingId: id.optional().nullable(),
  carryForward: z.boolean().default(true),
  visibleToClient: z.boolean().default(false),
});
export type CreateMeetingFullInput = z.infer<typeof createMeetingFullInput>;

export const updateMeetingFullInput = updateMeetingInput.extend({
  attendees: z.array(meetingAttendeeInput).max(200).optional(),
  /** Replaces the item list: rows whose id is listed are updated, others created, missing ones removed. */
  items: z.array(meetingItemInput).max(300).optional(),
  visibleToClient: z.boolean().optional(),
});
export type UpdateMeetingFullInput = z.infer<typeof updateMeetingFullInput>;

export const listMeetingActionItemsInput = z.object({
  projectId: id,
  status: meetingItemStatus.optional(),
  /** Only items owned by the caller. */
  mine: z.boolean().default(false),
});

// Team: sub users linked to their CRM company -----------------------------------

/** Links a workspace member's login to the CRM company they work for (`membership.company_id`). */
export const setMemberCompanyInput = z.object({ userId: id, companyId: id.nullable() });
export type SetMemberCompanyInput = z.infer<typeof setMemberCompanyInput>;

/** Adding someone to a project team can also link their login to their company. */
export const addProjectMemberFullInput = addProjectMemberInput.extend({ companyId: id.optional().nullable() });
export type AddProjectMemberFullInput = z.infer<typeof addProjectMemberFullInput>;

// RFIs -----------------------------------------------------------------------

/** A response; the project team may record one received from an outside contact (e.g. the architect by email). */
export const addRfiResponseFullInput = addRfiResponseInput.extend({
  attachments: z.array(rfiAttachmentSchema).max(20).default([]),
  onBehalfOfContactId: id.optional().nullable(),
});
export type AddRfiResponseFullInput = z.infer<typeof addRfiResponseFullInput>;

// Team chat (channels, messages, push tokens) ------------------------------------
// (Imported here rather than in the header list to keep this section self-contained.)
import { fileRef } from './common';

/** Most chat calls take one channel. */
export const chatChannelIdInput = z.object({ channelId: id });

/** Channels the caller belongs to; `projectId` narrows to one project's channels. */
export const listChatChannelsInput = z.object({ projectId: id.optional() });

export const createTradeChannelInput = z.object({
  projectId: id,
  /** The sub or supplier company whose users (on this project) join the channel. */
  companyId: id,
  name: z.string().trim().max(80).optional().nullable(),
  description: z.string().trim().max(500).optional().nullable(),
});
export type CreateTradeChannelInput = z.infer<typeof createTradeChannelInput>;

export const createGeneralChannelInput = z.object({
  name: nonEmpty(80),
  description: z.string().trim().max(500).optional().nullable(),
  memberUserIds: z.array(id).max(200).default([]),
});
export type CreateGeneralChannelInput = z.infer<typeof createGeneralChannelInput>;

/** Opens (or creates) the one-to-one channel with another workspace user. */
export const openDirectChannelInput = z.object({ userId: id });

export const chatChannelMembersInput = z.object({ channelId: id, userIds: z.array(id).min(1).max(100) });

export const listChatMessagesInput = z.object({
  channelId: id,
  /** Older page (newest-first keyset cursor from the previous page). */
  cursor: z.string().max(512).optional().nullable(),
  limit: z.number().int().min(1).max(100).default(40),
  /**
   * Polling: messages created, edited or deleted at or after this time (oldest first, deletions
   * included as `deleted: true`). Ignores `cursor`.
   */
  since: z.date().optional(),
});
export type ListChatMessagesInput = z.infer<typeof listChatMessagesInput>;

export const sendChatMessageInput = z
  .object({
    channelId: id,
    body: z.string().trim().max(4000).default(''),
    /** Files uploaded with `projects.chat.requestUpload` for this channel. */
    attachments: z.array(fileRef).max(10).default([]),
    replyToId: id.optional().nullable(),
  })
  .refine((v) => v.body.length > 0 || v.attachments.length > 0, { message: 'Write a message or attach a file', path: ['body'] });
export type SendChatMessageInput = z.infer<typeof sendChatMessageInput>;

export const editChatMessageInput = z.object({ id, body: nonEmpty(4000) });

export const muteChatChannelInput = z.object({ channelId: id, muted: z.boolean() });

export const chatRequestUploadInput = z.object({
  channelId: id,
  name: nonEmpty(255),
  contentType: z.string().max(255),
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(100 * 1024 * 1024),
});

export const registerPushTokenInput = z.object({
  /** Expo push token (`ExponentPushToken[...]`). */
  token: z.string().trim().min(10).max(255),
  platform: pushPlatform,
  deviceName: z.string().trim().max(120).optional().nullable(),
});
export type RegisterPushTokenInput = z.infer<typeof registerPushTokenInput>;

export const unregisterPushTokenInput = z.object({ token: z.string().trim().min(1).max(255) });

// Time clock (`time_entry`) -----------------------------------------------------

const costCodeText = z.string().trim().max(40).optional().nullable();
/** Breaks on one shift, in minutes (max 12 hours). */
const breakMinutes = z.number().int().min(0).max(720);

export const clockInInput = z.object({
  /**
   * Client-generated id. A retried clock-in (offline queue) with the same id returns the entry it
   * already created instead of failing.
   */
  id: id.optional(),
  /** Null or omitted for shop / overhead time. */
  projectId: id.optional().nullable(),
  scheduleTaskId: id.optional().nullable(),
  costCode: costCodeText,
  geo: geoPoint.optional().nullable(),
  /** When the worker tapped clock-in, for clock-ins queued offline (defaults to now). */
  at: isoDate.optional(),
  notes: optionalText(2000),
  source: timeEntrySource.default('mobile'),
});
export type ClockInInput = z.infer<typeof clockInInput>;

export const clockOutInput = z.object({
  /** The open entry; defaults to the caller's open entry. */
  id: id.optional(),
  geo: geoPoint.optional().nullable(),
  breakMinutes: breakMinutes.default(0),
  notes: optionalText(2000),
  /** When the worker tapped clock-out, for clock-outs queued offline (defaults to now). */
  at: isoDate.optional(),
});
export type ClockOutInput = z.infer<typeof clockOutInput>;

export const listTimeEntriesInput = z.object({
  userId: id.optional(),
  projectId: id.optional(),
  status: timeEntryStatus.optional(),
  /** Local calendar days (workspace time zone), inclusive. */
  from: dateOnly.optional(),
  to: dateOnly.optional(),
  includeArchived: z.boolean().default(false),
  limit: z.number().int().min(1).max(1000).default(500),
});
export type ListTimeEntriesInput = z.infer<typeof listTimeEntriesInput>;

export const myTimeEntriesInput = z.object({ from: dateOnly.optional(), to: dateOnly.optional() });

/** Weekly grid: one row per worker, one column per day, with the overtime split. */
export const timesheetInput = z.object({
  /** Any day of the week to show. */
  weekOf: dateOnly,
  userId: id.optional(),
  projectId: id.optional(),
  status: timeEntryStatus.optional(),
  /** Weekly hours before overtime (default 40). */
  overtimeThresholdHours: z.number().min(0).max(168).optional(),
});
export type TimesheetInput = z.infer<typeof timesheetInput>;

export const createTimeEntryInput = z.object({
  /** Defaults to the caller; managers may enter time for anyone. */
  userId: id.optional(),
  projectId: id.optional().nullable(),
  scheduleTaskId: id.optional().nullable(),
  costCode: costCodeText,
  clockInAt: isoDate,
  clockOutAt: isoDate,
  breakMinutes: breakMinutes.default(0),
  notes: optionalText(2000),
});
export type CreateTimeEntryInput = z.infer<typeof createTimeEntryInput>;

export const updateTimeEntryInput = z.object({
  id,
  expectedVersion,
  projectId: id.optional().nullable(),
  scheduleTaskId: id.optional().nullable(),
  costCode: costCodeText,
  clockInAt: isoDate.optional(),
  clockOutAt: isoDate.optional(),
  breakMinutes: breakMinutes.optional(),
  notes: optionalText(2000),
});
export type UpdateTimeEntryInput = z.infer<typeof updateTimeEntryInput>;

export const timeEntryIdsInput = z.object({ ids: z.array(id).min(1).max(200) });
export const rejectTimeEntriesInput = timeEntryIdsInput.extend({ reason: nonEmpty(1000) });

export const projectLaborCostInput = z.object({ projectId: id });

/** Hourly labour cost of a member for job costing (`membership.labor_rate_cents`); null = not costed. */
export const setLaborRateInput = z.object({ userId: id, laborRateCents: nonNegativeCents.max(100_000_00).nullable() });

export const payrollExportInput = z.object({
  from: dateOnly,
  to: dateOnly,
  /** Default: approved entries only. */
  status: timeEntryStatus.optional(),
  userId: id.optional(),
  projectId: id.optional(),
  overtimeThresholdHours: z.number().min(0).max(168).optional(),
});
export type PayrollExportInput = z.infer<typeof payrollExportInput>;

/** "My tasks" (field app): CRM tasks and schedule tasks assigned to the caller. */
export const MY_TASK_KINDS = ['task', 'schedule_task'] as const;
export const myTaskKind = z.enum(MY_TASK_KINDS);
export const myTasksInput = z.object({ includeDone: z.boolean().default(false) });
export const completeMyTaskInput = z.object({ kind: myTaskKind, id, done: z.boolean().default(true) });
